import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/material";
import { Loader, Typography } from "@trimble/modus-cmx";
import config from "@/config";
import { apiFetch } from "@/utils/apiFetch";
import { downloadResponse } from "@/utils/downloadBlob";
import type {
  DownloadStatus,
  FileDownloadRedirectProps,
  FileDownloadRouteParams,
} from "./types";

const RESOURCE_TYPE_CONFIG: Record<
  FileDownloadRedirectProps["resourceType"],
  { filenamePrefix: string; apiSegment: string }
> = {
  invoice: { filenamePrefix: "Invoice", apiSegment: "dealer-invoice" },
  "credit-memo": { filenamePrefix: "CreditMemo", apiSegment: "credit-memo" },
};

function getPdfDownloadFilename(
  resourceType: FileDownloadRedirectProps["resourceType"],
  id: string
): string {
  const { filenamePrefix } = RESOURCE_TYPE_CONFIG[resourceType];
  const timestamp = new Date().toISOString().replace(/\D/g, "").slice(0, 14);
  return `${filenamePrefix}_${id}_${timestamp}.pdf`;
}

export function FileDownloadRedirect({
  resourceType,
}: FileDownloadRedirectProps) {
  const { id, accountId } = useParams<FileDownloadRouteParams>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<DownloadStatus>("loading");

  useEffect(() => {
    if (!id) {
      return;
    }

    const controller = new AbortController();

    const url = new URL(
      `${RESOURCE_TYPE_CONFIG[resourceType].apiSegment}/${encodeURIComponent(id)}/download`,
      config.BSYS_DXP_API
    ).href;

    const fallbackFilename = getPdfDownloadFilename(resourceType, id);

    apiFetch(accountId, url, { method: "GET", signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        await downloadResponse(res, fallbackFilename);
        setStatus("done");
        navigate("/");
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return;
        setStatus("error");
      });

    return () => {
      controller.abort();
    };
  }, [id, accountId, resourceType, navigate]);

  const resolvedStatus = id ? status : "error";

  if (resolvedStatus === "done") {
    return null;
  }

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1}
      sx={{ height: "100vh" }}
    >
      {resolvedStatus === "loading" && (
        <>
          <Loader size="lg" />
          <Typography hierarchy="p" weight="bold" size="2xl">
            {t("file_download.preparing")}
          </Typography>
        </>
      )}
      {resolvedStatus === "error" && (
        <Typography hierarchy="p" size="xl">
          {t("file_download.error")}
        </Typography>
      )}
    </Stack>
  );
}
